/**
 * Pinia Store для UI состояния
 * Тосты, модальные окна, тема и другие UI элементы
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Toast, NavItem } from '@/types'

// Навигационные пункты
const mainNavItems: NavItem[] = [
  { name: 'Главная', path: '/' },
  { name: 'Блог', path: '/blog' },
]

const authNavItems: NavItem[] = [
  { name: 'Мой дневник', path: '/diary', requiresAuth: true },
  { name: 'Профиль', path: '/profile', requiresAuth: true },
]

const adminNavItems: NavItem[] = [
  { name: 'Админ-панель', path: '/admin', requiresAuth: true, requiresAdmin: true },
]

export const useUIStore = defineStore('ui', () => {
  // ==================== STATE ====================
  
  // Тосты
  const toasts = ref<Toast[]>([])
  
  // Модальные окна
  const activeModal = ref<string | null>(null)
  const modalProps = ref<Record<string, unknown>>({})
  
  // Тема
  const isDarkMode = ref(false)
  
  // Сайдбар
  const isSidebarOpen = ref(false)
  
  // Поиск
  const isSearchOpen = ref(false)
  
  // Загрузка глобальная
  const isGlobalLoading = ref(false)

  // ==================== GETTERS ====================
  
  const hasToasts = computed(() => toasts.value.length > 0)
  const isModalOpen = computed(() => !!activeModal.value)
  
  // Фильтр навигации по авторизации
  const getNavItems = (isAuthenticated: boolean, isAdmin: boolean) => {
    return [
      ...mainNavItems,
      ...authNavItems.filter(item => !item.requiresAuth || isAuthenticated),
      ...adminNavItems.filter(item => isAuthenticated && isAdmin),
    ]
  }

  // ==================== ACTIONS ====================
  
  /**
   * Добавление тоста
   */
  function showToast(
    message: string, 
    type: Toast['type'] = 'info', 
    duration: number = 5000
  ): void {
    const id = Date.now().toString()
    const toast: Toast = { id, message, type, duration }
    
    toasts.value.push(toast)
    
    // Автоматическое удаление
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }
  }

  /**
   * Показать success тост
   */
  function showSuccess(message: string, duration?: number): void {
    showToast(message, 'success', duration)
  }

  /**
   * Показать error тост
   */
  function showError(message: string, duration?: number): void {
    showToast(message, 'error', duration)
  }

  /**
   * Показать warning тост
   */
  function showWarning(message: string, duration?: number): void {
    showToast(message, 'warning', duration)
  }

  /**
   * Удаление тоста
   */
  function removeToast(id: string): void {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  /**
   * Очистка всех тостов
   */
  function clearToasts(): void {
    toasts.value = []
  }

  /**
   * Открытие модального окна
   */
  function openModal(name: string, props: Record<string, unknown> = {}): void {
    activeModal.value = name
    modalProps.value = props
  }

  /**
   * Закрытие модального окна
   */
  function closeModal(): void {
    activeModal.value = null
    modalProps.value = {}
  }

  /**
   * Переключение темы
   */
  function toggleTheme(): void {
    isDarkMode.value = !isDarkMode.value
    
    // Сохраняем в localStorage
    localStorage.setItem('vibe_diary_theme', isDarkMode.value ? 'dark' : 'light')
    
    // Применяем класс к body
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  /**
   * Установка темы
   */
  function setTheme(dark: boolean): void {
    isDarkMode.value = dark
    localStorage.setItem('vibe_diary_theme', dark ? 'dark' : 'light')
    
    if (dark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  /**
   * Загрузка темы из localStorage
   */
  function loadTheme(): void {
    const savedTheme = localStorage.getItem('vibe_diary_theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    
    isDarkMode.value = savedTheme === 'dark' || (!savedTheme && prefersDark)
    
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark')
    }
  }

  /**
   * Переключение сайдбара
   */
  function toggleSidebar(): void {
    isSidebarOpen.value = !isSidebarOpen.value
  }

  /**
   * Закрытие сайдбара
   */
  function closeSidebar(): void {
    isSidebarOpen.value = false
  }

  /**
   * Открытие поиска
   */
  function openSearch(): void {
    isSearchOpen.value = true
  }

  /**
   * Закрытие поиска
   */
  function closeSearch(): void {
    isSearchOpen.value = false
  }

  /**
   * Установка глобальной загрузки
   */
  function setGlobalLoading(loading: boolean): void {
    isGlobalLoading.value = loading
  }

  return {
    // State
    toasts,
    activeModal,
    modalProps,
    isDarkMode,
    isSidebarOpen,
    isSearchOpen,
    isGlobalLoading,
    
    // Getters
    hasToasts,
    isModalOpen,
    getNavItems,
    
    // Actions
    showToast,
    showSuccess,
    showError,
    showWarning,
    removeToast,
    clearToasts,
    openModal,
    closeModal,
    toggleTheme,
    setTheme,
    loadTheme,
    toggleSidebar,
    closeSidebar,
    openSearch,
    closeSearch,
    setGlobalLoading,
  }
})
